import React, {useState} from "react"
import {useParams} from "react-router-dom"
import {
  Col,
  Container,
  FormControl,
  FormGroup,
  FormLabel,
  Row
} from "react-bootstrap"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {Form, Formik} from 'formik'
import {inject, observer} from "mobx-react"
import {resolveIdentifier} from "mobx-state-tree"
import {WithTranslation, withTranslation} from "react-i18next"
import AlertMessage from "../../components/AlertMessage"
import {
  MultiLanguageFormikTitleField,
  MultiLanguageFormikDescriptionField
} from "../../components/Formik"
import ImageUploadSection from '../../components/ImageUploadSection'
import SelectDropdown from '../../components/SelectDropdown'
import ToggleWithModal from '../../components/ToggleWithModal'
import VenueSelectionDropdown from '../../components/VenueSelectionDropdown'
import { ExternalPOSProductManager } from '../../components/ExternalPOSProductManager'
import { RewardArticleSelector } from '../../components/RewardArticleSelector'
import FormModals from '../../components/FormModals'
import { FormActionButtons } from '../../components/FormActionButtons'
import { FormHeader } from '../../components/FormHeader'
import { FormSection } from '../../components/FormSection'
import {MerchantAdminApi} from "../../Api"
import {RewardsLocation, StampCardModifyLocationParamsProps} from "../../Locations"
import {ILoyaltyCard, LoyaltyCard} from "../../models/LoyaltyCard"
import {ILoyaltyCardStore} from "../../models/LoyaltyCardStore"
import {IProductStore} from "../../models/ProductStore"
import {IUserSessionStore} from "../../models/UserSessionStore"
import {colors} from "../../theme/colors"
import { useLanguageService } from "../../utils/languageContext"
import { uploadImage, ImageType } from '../../utils/imageUpload'
import SaveAndPublishButton from '../../components/SaveAndPublishButton'
import { 
  getStampCardValidationSchema, 
  getStampCardDefaultValues, 
  transformStampCardValues,
  prepareStampCardForSubmission
} from './formUtils'

const CREATE_UPDATE_STAMPCARD_ERROR = "createUpdateStampCardError"

export interface CreateUpdateStampCardScreenProps extends WithTranslation {
  api?: MerchantAdminApi
  userSessionStore?: IUserSessionStore
  productStore?: IProductStore
  loyaltyCardStore?: ILoyaltyCardStore
  history?: any
}

const CreateUpdateStampCardScreen = (props: CreateUpdateStampCardScreenProps) => {
  const [showGoBackModal, setShowGoBackModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const params = useParams<StampCardModifyLocationParamsProps>()

  const selectedStampCardId = params.stampCardId
  const selectedStampCard: ILoyaltyCard | undefined = resolveIdentifier(LoyaltyCard,
    props.loyaltyCardStore!.loyaltyCards!,
    selectedStampCardId)

  const {t, userSessionStore, history, api} = props
  // Use the language service hook instead of creating a new instance
  const languageService = useLanguageService();

  // Use language service methods
  const contentLanguages = languageService.getContentLanguages();
  
  const validationSchema = getStampCardValidationSchema(t, languageService);
  
  const initValues = selectedStampCard 
    ? transformStampCardValues(selectedStampCard)
    : getStampCardDefaultValues(userSessionStore!, languageService);

  const handleSaveAndPublish = async (values: any) => {
    setSaveError(null);
    try {
      const postData = prepareStampCardForSubmission(values);
      const response = await api?.postStampCard(postData, undefined, CREATE_UPDATE_STAMPCARD_ERROR)
      if (response) {
        await props.loyaltyCardStore?.updateLoyaltyCard(response)
        history.push(RewardsLocation.toUrl(userSessionStore!.selectedBranch!.urlFields()))
      }
    } catch (error) {
      console.error('Error saving stamp card:', error);
      setSaveError(t('stampCardModifyScreen.errors.saveError'));
    }
  }
  
  const onUploadStampCardImage = async (imageFile: any) => {
    return uploadImage(api, imageFile, ImageType.VENUE_PRODUCT_MAIN, 'stamp card');
  };

  const onBackToRewards = (isFormEdited: boolean) => {
    if (isFormEdited)
      setShowGoBackModal(true)
    else
      history.push(RewardsLocation.toUrl(userSessionStore!.selectedBranch!.urlFields()))
  }

  return (
    <Container>
      {saveError && (
        <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
          <FontAwesomeIcon className="me-2" icon={["fas", "exclamation-circle"]} />
          <div>{saveError}</div>
        </div>
      )}

      <Formik
        initialValues={initValues}
        validationSchema={validationSchema}
        onSubmit={values => {
          handleSaveAndPublish(values)
        }}
      >
        {({errors, touched, values, dirty, handleChange, resetForm, setFieldValue}) => {
          const handleRemoveArticle = (articleId: string) => {
            setFieldValue('settings.validItems.items',
              (values.settings.validItems.items as number[])
                .map(String)
                .filter(id => id !== articleId)
            );
          };

          const formTitle = selectedStampCard 
            ? String(selectedStampCard.localeName() || t(`stampCardModifyScreen.formTitle`))
            : t(`stampCardModifyScreen.formTitle`);

          return (
            <Form>
              <FormModals
                showGoBackModal={showGoBackModal}
                showRevertModal={showRevertModal}
                onHideGoBackModal={() => setShowGoBackModal(false)}
                onHideRevertModal={() => setShowRevertModal(false)}
                onConfirmGoBack={() => history.push(RewardsLocation.toUrl(userSessionStore!.selectedBranch!.urlFields()))}
                onConfirmRevert={() => {
                  resetForm();
                  setShowRevertModal(false);
                }}
              />

              <FormActionButtons
                onBack={() => onBackToRewards(dirty)}
                onRevert={() => setShowRevertModal(true)}
                onSubmit={() => {}}
                isFormDirty={dirty}
                backButtonText={t("stampCardModifyScreen.backToRewards")}
              />

              <FormHeader 
                title={formTitle}
                description={t(`stampCardModifyScreen.formDescription`).split('\n')}
              />

              {!userSessionStore!.currentOrganization!.isSingleVenue() && (
                <FormSection title={t('rewardModifyScreen.venues')}>
                  <Row>
                    <Col md={6}>
                      <VenueSelectionDropdown
                        assignedVenues={values.restaurantIds}
                        setFieldValue={setFieldValue}
                        venues={userSessionStore!.currentOrganization!.venues!}
                        fieldName="restaurantIds"
                      />
                    </Col>
                  </Row>
                </FormSection>
              )}

              <FormSection title={t('stampCardModifyScreen.basics')}>
                <Row>
                  <Col md={6}>
                    <MultiLanguageFormikTitleField
                      baseName="name.text"
                      errors={errors}
                      touched={touched}
                      values={values}
                      onChange={handleChange}
                      placeholderTextKey="stampCardModifyScreen.titlePlaceholder"
                      labelTextKey="stampCardModifyScreen.titleField"
                      showLabel={true}
                    />
                  </Col>
                </Row>

                <Row className="align-items-center">
                  <Col md={3}>
                    <Container className="p-0">
                      <Row>
                        <Col md={12}>
                          <SelectDropdown
                            name="settings.cap"
                            label={t('stampCardModifyScreen.stampsNeeded')}
                            values={values}
                            errors={errors}
                            touched={touched}
                            onChange={handleChange}
                            setFieldValue={setFieldValue}
                            options={[10, 9, 8, 7, 6, 5, 4].map(value => ({
                              value,
                              label: `${value-1} stamps`
                            }))}
                          />
                        </Col>
                      </Row>
                    </Container>
                  </Col>
                </Row>

                <Row>
                  <Col md={11}>
                    <div className="m-0 font-size-small text-uppercase mb-3" style={{
                      paddingLeft: 0,
                      borderBottom: `1px solid ${colors.stroke}`
                    }}>{t('stampCardModifyScreen.eligibleProducts')}</div>
                    <p className="m-0 font-size-small mb-3">{t('stampCardModifyScreen.eligibleProductsDescription')}</p>
                    {api && (
                      <ExternalPOSProductManager
                        api={api}
                        articleIds={(values.settings.validItems.items as number[]).map(String)}
                        onRemoveArticle={handleRemoveArticle}
                        onAddArticle={(articleId) => {
                          const newItems = [...values.settings.validItems.items, articleId];
                          setFieldValue('settings.validItems.items', newItems);
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </FormSection>

              <FormSection title={t('rewardModifyScreen.reward')}>
                <Row>
                  <Col md={6}>
                    <MultiLanguageFormikTitleField
                      baseName="reward_data.title"
                      errors={errors}
                      touched={touched}
                      values={values}
                      onChange={handleChange}
                      placeholderTextKey="stampCardModifyScreen.rewardTitlePlaceholder"
                      labelTextKey="stampCardModifyScreen.rewardTitleField"
                      showLabel={true}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <MultiLanguageFormikDescriptionField
                      baseName="reward_data.description"
                      errors={errors}
                      touched={touched}
                      values={values}
                      onChange={handleChange}
                      placeholderTextKey="descriptionPlaceholder"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={11}>
                    <div className="m-0 font-size-small text-uppercase mb-3" style={{
                      paddingLeft: 0,
                      borderBottom: `1px solid ${colors.stroke}`
                    }}>{t('stampCardModifyScreen.rewardArticle')}</div>
                    <p className="m-0 font-size-small mb-3">{t('stampCardModifyScreen.rewardArticleDescription')}</p>
                    {api && (
                      <RewardArticleSelector
                        api={api}
                        articleId={values.reward_data.properties.articleId ? String(values.reward_data.properties.articleId) : null}
                        initialArticleId={selectedStampCard?.reward_data?.properties?.articleId ? String(selectedStampCard.reward_data.properties.articleId) : null}
                        onArticleSelected={(articleId) => {
                          setFieldValue('reward_data.properties.articleId', articleId);
                        }}
                      />
                    )}
                    <div className="mt-3">
                      <FormGroup controlId="reward_data.properties.priceGroupId">
                        <FormLabel className="textInputLabel">
                          {t('stampCardModifyScreen.priceGroupId')}
                        </FormLabel>
                        <FormControl
                          className="textInput"
                          type="text"
                          placeholder={t('stampCardModifyScreen.priceGroupIdPlaceholder')}
                          value={values.reward_data.properties.priceGroupId}
                          onChange={(e) => setFieldValue('reward_data.properties.priceGroupId', e.target.value)}
                          style={{ width: '100px' }}
                        />
                        <small className="text-muted">{t('stampCardModifyScreen.priceGroupIdHint')}</small>
                      </FormGroup>
                    </div>
                  </Col>
                </Row>

                <ImageUploadSection
                  name="reward_data.images.mainImage"
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  onImageUpload={onUploadStampCardImage}
                />

                <AlertMessage source="imageUploadError" />

                <FormGroup controlId="status">
                  <ToggleWithModal
                    name="status"
                    value={values.status}
                    onValue="DISABLED"
                    offValue="ENABLED"
                    onChange={(newStatus) => setFieldValue('status', newStatus)}
                    label={t("stampCardModifyScreen.hideRewardFromUsers")}
                    modalTitle={t("stampCardModifyScreen.modals.hideReward.title")}
                    modalDescription={t("stampCardModifyScreen.modals.hideReward.description")}
                    modalConfirmText={t("manageVenuesScreen.hide")}
                    modalCancelText={t("manageVenuesScreen.cancel")}
                    className="mt-5"
                  />
                </FormGroup>

                <Row>
                  <Col md={6}>
                    <SaveAndPublishButton className="mt-4" />
                  </Col>
                </Row>
              </FormSection>
            </Form>
          )
        }}
      </Formik>
    </Container>
  )
}

export default withTranslation()(inject("userSessionStore",
  "api",
  "productStore",
  "loyaltyCardStore")(
  observer(CreateUpdateStampCardScreen))) 